import { useQuery } from '@tanstack/react-query';
import { loyaltyLaneClient } from '@/services/api/loyalty-lane-client';
import { useUserStore } from '@/store/useUserStore';
import { TokenStorage } from '../lib/token-storage';
import type { LLApiResponse, Card } from '../types/loyalty-lane';

/**
 * The signed-in shopper's loyalty card number.
 *
 * `couponProfile.cardNumber` is only populated when an account is *created* on
 * this browser (the registration broker returns it) — a normal sign-in leaves it
 * empty. So the LL Card endpoint is the real source, with the cached value kept
 * as a fallback for offline / in-flight reads.
 *
 * @param enabled gate the fetch, e.g. to when the containing sheet is open.
 */
export function useLoyaltyCard(enabled = true) {
  const isAuthenticated = TokenStorage.isAuthenticated();
  const cached = useUserStore((s) => s.couponProfile?.cardNumber);

  const { data, isLoading } = useQuery({
    queryKey: ['loyalty-card'],
    queryFn: async () => {
      const response = await loyaltyLaneClient.get<LLApiResponse<{ card: Card }>>('Card');
      const card = response.data.data?.card;
      if (!card) return '';
      // API returns `Number` (numeric), not `CardNumber` (string).
      const num = (card as unknown as Record<string, unknown>).Number ?? card.CardNumber;
      return num != null ? String(num) : '';
    },
    enabled: enabled && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  return {
    cardNumber: data || cached || '',
    isLoading: enabled && isAuthenticated && isLoading,
  };
}
